import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { sessionQueryKey } from '@/entities/session';
import { register } from '@/shared/api/auth';
import type { User } from '@/shared/api/types';
import { currentLanguage } from '@/shared/i18n';
import { Button, ErrorMessage, PasswordField, PasswordStrength, TextField } from '@/shared/ui';

import { isUsernameShapeValid } from '../model/useUsernameCheck';
import styles from './forms.module.css';
import { UsernameField } from './UsernameField';

import uiStyles from '@/shared/ui/ui.module.css';

/**
 * Sign-up with the minimum that is needed: email, username, password and
 * consent. The display name, avatar and the rest are set later in settings.
 */
export function RegisterForm({
  onSuccess,
}: {
  onSuccess?: (user: User) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [consented, setConsented] = useState(false);

  const mutation = useMutation({
    mutationFn: () => register({ email, username, password, consented, locale: currentLanguage() }),
    onSuccess: (user) => {
      queryClient.setQueryData(sessionQueryKey, user);
      onSuccess?.(user);
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <TextField
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
      />
      <UsernameField value={username} onChange={setUsername} />
      <PasswordField
        label={t('auth.password')}
        autoComplete="new-password"
        required
        minLength={8}
        hint={t('auth.passwordHint')}
        value={password}
        onChange={(event) => {
          setPassword(event.target.value);
        }}
      />
      <PasswordStrength password={password} userInputs={[email, username]} />
      <label className={uiStyles.check}>
        <input
          type="checkbox"
          required
          checked={consented}
          onChange={(event) => {
            setConsented(event.target.checked);
          }}
        />
        <span>
          <Trans
            i18nKey="auth.consent"
            components={{
              terms: <Link to="/terms" target="_blank" />,
              privacy: <Link to="/privacy" target="_blank" />,
            }}
          />
        </span>
      </label>
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      <Button
        type="submit"
        variant="primary"
        disabled={mutation.isPending || !consented || !isUsernameShapeValid(username)}
      >
        {t('auth.submitRegister')}
      </Button>
    </form>
  );
}
